import React, { useState } from 'react';
import axios from 'axios';
import { PdsBox, PdsMantineText, PdsMantineButton } from '@podium-design-system/react-components';
import CaseEditModal from './CaseEditModal';

function closeParentDetails(ev) {
  const el = ev.currentTarget && ev.currentTarget.closest('details');
  if (el) el.open = false;
}

/**
 * Ellipsis menu: edit, pause/unpause, complete/reopen, delete.
 * Use next to View / Open case on lists (Cases page, home existing-case cards).
 */
export default function CaseCompactActions({ caseRow, onRefreshList }) {
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const st = String(caseRow.status || '').toUpperCase();

  const putStatus = async (newStatus, ev) => {
    closeParentDetails(ev);
    setBusy(true);
    setError(null);
    try {
      await axios.put(`/api/cases/${caseRow.id}/status`, { status: newStatus });
      onRefreshList();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Update failed.');
    } finally {
      setBusy(false);
    }
  };

  const deleteCase = async (ev) => {
    closeParentDetails(ev);
    const label = caseRow.target || caseRow.title || 'this case';
    if (!window.confirm(`Are you sure you want to delete the case for "${label}"? This action cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(`/api/cases/${caseRow.id}`);
      onRefreshList();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (ev) => {
    closeParentDetails(ev);
    setEditOpen(true);
    setError(null);
  };

  return (
    <>
      <PdsBox
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="xs"
        style={{ flexShrink: 0 }}
        data-case-card-stop=""
      >
        {error && (
          <PdsMantineText type="textline" component="span" style={{ fontSize: 12, color: '#991b1b', maxWidth: 200, textAlign: 'right' }}>
            {error}
          </PdsMantineText>
        )}
        <details
          className="case-compact-actions"
          style={{ position: 'relative' }}
          onToggle={(e) => {
            if (e.target.open) setError(null);
          }}
        >
          <summary
            style={{
              listStyle: 'none',
              cursor: busy ? 'wait' : 'pointer',
              padding: '6px 8px',
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
            }}
            title="Quick actions"
            aria-label="Quick actions"
          >
            <i className="fas fa-ellipsis-vertical" style={{ fontSize: 14, color: '#57606a' }} aria-hidden />
          </summary>
          <PdsBox
            direction="column"
            gap="xs"
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 4,
              minWidth: 180,
              padding: 8,
              background: '#fff',
              border: '1px solid #d0d7de',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              zIndex: 20,
            }}
          >
            <PdsMantineButton rank="outline" color="neutral" size="sm" stretch disabled={busy} onClick={openEdit}>
              Edit case
            </PdsMantineButton>
            {st === 'ACTIVE' && (
              <PdsMantineButton rank="outline" color="warning" size="sm" stretch disabled={busy} onClick={(ev) => putStatus('PAUSED', ev)}>
                Pause
              </PdsMantineButton>
            )}
            {st === 'PAUSED' && (
              <PdsMantineButton rank="outline" color="warning" size="sm" stretch disabled={busy} onClick={(ev) => putStatus('ACTIVE', ev)}>
                Unpause
              </PdsMantineButton>
            )}
            {(st === 'ACTIVE' || st === 'PAUSED') && (
              <PdsMantineButton rank="outline" color="action" size="sm" stretch disabled={busy} onClick={(ev) => putStatus('COMPLETED', ev)}>
                Complete
              </PdsMantineButton>
            )}
            {st === 'COMPLETED' && (
              <>
                <PdsMantineButton rank="outline" color="warning" size="sm" stretch disabled={busy} onClick={(ev) => putStatus('PAUSED', ev)}>
                  Pause
                </PdsMantineButton>
                <PdsMantineButton rank="outline" color="action" size="sm" stretch disabled={busy} onClick={(ev) => putStatus('ACTIVE', ev)}>
                  Reopen
                </PdsMantineButton>
              </>
            )}
            <PdsMantineButton rank="outline" color="attention" size="sm" stretch disabled={busy} onClick={(ev) => deleteCase(ev)}>
              Delete
            </PdsMantineButton>
          </PdsBox>
        </details>
      </PdsBox>
      {editOpen && (
        <CaseEditModal
          caseData={caseRow}
          onClose={() => setEditOpen(false)}
          onSuccess={() => {
            setEditOpen(false);
            onRefreshList();
          }}
        />
      )}
    </>
  );
}
